import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navLinks, brandLogo } from '../mock';
import { Instagram, Facebook, ChevronDown, Menu, X } from 'lucide-react';
import JobNotification from './JobNotification';
import LeftActionPanel from './LeftActionPanel';

const WhatsAppIcon = ({ size = 18, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
  >
    <path d="M20.52 3.48A11.85 11.85 0 0 0 12 0C5.37 0 0 5.37 0 12a11.9 11.9 0 0 0 1.64 6.03L0 24l6.16-1.61A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52ZM12 21.82c-1.86 0-3.68-.5-5.28-1.44l-.38-.23-3.66.96.98-3.56-.25-.37A9.82 9.82 0 1 1 21.82 12c0 5.42-4.4 9.82-9.82 9.82Zm5.4-7.36c-.3-.15-1.75-.87-2.02-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.66-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.34.44-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.58-.48-.5-.66-.5h-.56c-.2 0-.5.07-.76.37-.27.3-1.03 1-1.03 2.45s1.06 2.83 1.2 3.03c.15.2 2.08 3.18 5.05 4.46.7.3 1.26.48 1.7.62.71.23 1.36.2 1.87.12.57-.08 1.75-.71 2-1.4.25-.7.25-1.3.18-1.42-.07-.13-.27-.2-.57-.35Z" />
  </svg>
);

const Navbar = () => {
  const [visible, setVisible] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleGetStarted = () => navigate('/contact');

  return (
    <>
      <LeftActionPanel />
      <JobNotification />
      {/* Non-sticky top bar (scrolls away with hero) */}
      <div
        className="absolute top-0 left-0 right-0 z-30 px-6 md:px-14 py-4 md:py-5 flex items-center justify-between"
        data-testid="top-navbar"
      >
        <Link to="/" className="flex items-center gap-2 opacity-0 pointer-events-none" aria-hidden>
          {/* Spacer to preserve layout - real sticky logo rendered outside */}
          <img src={brandLogo} alt="" className="h-10 md:h-12 w-auto object-contain" />
        </Link>

        <div className="flex items-center gap-3 md:gap-4">
          <div className="hidden md:inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full bg-white/70 border border-[#143324]/10 backdrop-blur-sm">
            <span className="relative flex w-2.5 h-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-70 pulse-dot" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
            <span className="text-[13px] text-[#1c1c1c] font-body">enrolling now</span>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/70 border border-[#143324]/10 flex items-center justify-center text-[#143324] hover:bg-white transition-colors">
              <Instagram size={16} strokeWidth={1.6} />
            </a>
            <a href="https://wa.me/917010492161" target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 rounded-full bg-white/70 border border-[#143324]/10 flex items-center justify-center text-[#143324] hover:bg-white transition-colors">
              <WhatsAppIcon size={15} />
            </a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/70 border border-[#143324]/10 flex items-center justify-center text-[#143324] hover:bg-white transition-colors">
              <Facebook size={16} strokeWidth={1.6} />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            className="md:hidden w-10 h-10 rounded-full bg-white/70 border border-[#143324]/10 flex items-center justify-center text-[#143324]"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Sticky logo only — remains pinned as page scrolls */}
      <Link
        to="/"
        aria-label="Eduvantage home"
        data-testid="sticky-logo"
        className={`fixed top-4 md:top-5 left-6 md:left-14 z-40 flex items-center gap-2 transition-all duration-300 ${
          visible ? 'drop-shadow-md' : ''
        }`}
      >
        <img src={brandLogo} alt="Eduvantage Solutions" className="h-10 md:h-12 w-auto object-contain" />
      </Link>

      {/* Mobile menu drawer */}
      <div
        className={`md:hidden fixed top-[68px] left-0 right-0 z-30 px-6 transition-all duration-300 ${
          mobileOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="bg-[#f5f1e6] border border-[#143324]/10 rounded-2xl p-3 shadow-xl">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="mb-1">
                <div className="px-4 pt-2 pb-1 text-[11px] tracking-wider uppercase text-[#3d3d3d] font-body">
                  {link.name}
                </div>
                {link.children.map((group) => (
                  <div key={group.title} className="mb-2 last:mb-0">
                    <div className="px-4 pt-2 pb-1 text-[10px] tracking-wider uppercase text-[#143324]/70 font-body">
                      {group.title}
                    </div>
                    {group.links.map((c) => (
                      <Link
                        key={c.name}
                        to={c.href}
                        onClick={() => setMobileOpen(false)}
                        className={`block px-4 py-2.5 rounded-xl text-[14px] font-body ${
                          isActive(c.href) ? 'bg-[#143324] text-[#f5f1e6]' : 'text-[#1c1c1c] hover:bg-[#e8e2d1]'
                        }`}
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-2.5 rounded-xl text-[14px] font-body ${
                  isActive(link.href) ? 'bg-[#143324] text-[#f5f1e6]' : 'text-[#1c1c1c] hover:bg-[#e8e2d1]'
                }`}
              >
                {link.name}
              </Link>
            )
          )}
          <button
            onClick={() => {
              setMobileOpen(false);
              handleGetStarted();
            }}
            className="w-full mt-2 px-5 py-3 rounded-xl btn-primary text-[14px] font-body"
          >
            Enroll Now
          </button>
        </div>
      </div>

      {/* Sticky floating pill nav (bottom center) — always visible on desktop */}
      <div
        className="hidden md:block fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
        data-testid="bottom-nav-pill"
      >
        <div className="nav-pill rounded-full px-3 py-2 flex items-center gap-1 md:gap-2 shadow-xl">
          {navLinks.map((link) => {
            if (link.children) {
              return (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <button
                    className={`px-3 md:px-4 py-2 rounded-full text-sm inline-flex items-center gap-1 transition-colors duration-200 font-body ${
                      isActive(link.href) ? 'bg-[#e8e2d1] text-[#143324]' : 'text-[#1c1c1c] hover:bg-[#e8e2d1]'
                    }`}
                  >
                    {link.name}
                    <ChevronDown size={13} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div
                    className={`absolute bottom-full left-1/2 -translate-x-1/2 pb-4 w-[min(900px,calc(100vw-48px))] transition-all duration-200 ${
                      dropdownOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'
                    }`}
                  >
                    <div className="bg-[#f5f1e6] border border-[#143324]/10 rounded-2xl shadow-2xl overflow-hidden">
                      <div className="grid grid-cols-4">
                        {link.children.map((group, groupIndex) => (
                          <div
                            key={group.title}
                            className={`p-5 ${group.cta ? 'bg-white/55' : ''} ${groupIndex > 0 ? 'border-l border-[#143324]/10' : ''}`}
                          >
                            <p className="mb-4 text-[10px] font-body font-semibold uppercase tracking-[0.18em] text-[#143324]/70">
                              {group.title}
                            </p>
                            <div className="space-y-1.5">
                              {group.links.map((c) => (
                                <Link
                                  key={c.name}
                                  to={c.href}
                                  className={`block rounded-xl px-3 py-2.5 transition-colors ${
                                    isActive(c.href) ? 'bg-[#143324] text-[#f5f1e6]' : 'text-[#1c1c1c] hover:bg-[#e8e2d1]'
                                  }`}
                                >
                                  <span className={`block text-[13px] font-body font-semibold ${isActive(c.href) ? 'text-[#f5f1e6]' : 'text-[#143324]'}`}>
                                    {c.name}
                                  </span>
                                  <span className={`mt-1 block text-[11.5px] font-body leading-relaxed ${isActive(c.href) ? 'text-[#e8e2d1]' : 'text-[#3d3d3d]'}`}>
                                    {c.desc}
                                  </span>
                                </Link>
                              ))}
                            </div>
                            {group.cta && (
                              <button
                                onClick={handleGetStarted}
                                className="mt-5 w-full rounded-full btn-primary px-4 py-2.5 text-[13px] font-body"
                              >
                                Enroll Now
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={link.name}
                to={link.href}
                className={`px-3 md:px-4 py-2 rounded-full text-sm font-body transition-colors duration-200 ${
                  isActive(link.href) ? 'bg-[#e8e2d1] text-[#143324]' : 'text-[#1c1c1c] hover:bg-[#e8e2d1]'
                }`}
              >
                {link.name}
              </Link>
            );
          })}
          <button
            onClick={handleGetStarted}
            className="ml-1 px-4 md:px-5 py-2 rounded-full btn-primary text-sm font-body"
            data-testid="nav-enroll-cta"
          >
            Enroll Now
          </button>
        </div>
      </div>
    </>
  );
};

export default Navbar;
